/**
 * CompliancePane — v2.12.11
 *
 * Honest, self-attested compliance registry. Shows every privacy /
 * security / residency framework Konnect Migration Studio aligns with,
 * mapped against concrete platform controls with clear status labels
 * (Aligned / In Progress / Planned).
 *
 * No fake seals — every claim links to the underlying control + evidence
 * + audit roadmap. Serious customers can download the Compliance
 * Statement (plain text) via /api/compliance/statement.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, Loader2, CheckCircle2, Clock, Circle, Info, Download,
  Globe, ScrollText, Lock, FileText, ChevronRight, ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  aligned:        { label: 'Aligned',       color: 'emerald', Icon: CheckCircle2 },
  in_progress:    { label: 'In Progress',   color: 'amber',   Icon: Clock },
  planned:        { label: 'Planned',       color: 'slate',   Icon: Circle },
  not_applicable: { label: 'Not Applicable', color: 'slate',  Icon: Circle },
  implemented:    { label: 'Implemented',   color: 'emerald', Icon: CheckCircle2 },
  complete:       { label: 'Complete',      color: 'emerald', Icon: CheckCircle2 },
};

const REGION_FLAGS = {
  Bahrain: '🇧🇭', 'Kingdom of Saudi Arabia': '🇸🇦', 'United Arab Emirates': '🇦🇪',
  Qatar: '🇶🇦', 'Sultanate of Oman': '🇴🇲',
  Philippines: '🇵🇭', Singapore: '🇸🇬',
  'EU + EEA': '🇪🇺', 'United Kingdom': '🇬🇧',
  Global: '🌐', 'Global (US-anchored)': '🌐',
};

function StatusPill({ status, size = 'sm' }) {
  const meta = STATUS_META[status] || STATUS_META.planned;
  const cls = {
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    amber:   'bg-amber-100 text-amber-800 border-amber-200',
    slate:   'bg-slate-100 text-slate-700 border-slate-200',
  }[meta.color];
  const px = size === 'xs' ? 'text-[9px] px-1.5 py-0.5' : 'text-[10px] px-2 py-0.5';
  const Ic = meta.Icon;
  return (
    <Badge className={`${cls} ${px} border font-medium inline-flex items-center gap-1`}>
      <Ic className="w-3 h-3" /> {meta.label}
    </Badge>
  );
}

export default function CompliancePane() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ aligned: 0, in_progress: 0, planned: 0 });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // framework detail modal

  useEffect(() => {
    axios.get(`${API}/api/compliance/frameworks`)
      .then((r) => { setRows(r?.data?.frameworks || []); setCounts(r?.data?.counts || {}); })
      .catch((err) => toast.error(err?.response?.data?.detail || 'Failed to load compliance registry'))
      .finally(() => setLoading(false));
  }, []);

  const downloadStatement = async () => {
    try {
      const { data } = await axios.get(`${API}/api/compliance/statement`);
      const blob = new Blob([data.content || ''], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `konnect-compliance-statement-${new Date().toISOString().slice(0, 10)}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  if (loading) {
    return <div className="p-6 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading compliance registry…</div>;
  }

  return (
    <div className="space-y-4" data-testid="compliance-pane">
      {/* Top summary card */}
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-slate-50">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
            <CardTitle className="text-base sm:text-lg">Konnect Migration Studio — Compliance Registry</CardTitle>
            <Badge className="bg-slate-900 text-white text-[10px]">Self-Attested · v2.12.11</Badge>
          </div>
          <p className="text-xs text-slate-600 mt-1 max-w-3xl">
            Konnect operates as a <b>Data Processor</b> (or the local equivalent — PIP, Data Intermediary) under
            every framework listed below. Each claim links to the specific platform control that backs it.
            Formal third-party audits (ISO 27001, SOC 2 Type II) have realistic roadmap dates — no fake seals.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-white border border-emerald-200" data-testid="summary-aligned">
              <div className="text-2xl font-bold text-emerald-800">{counts.aligned || 0}</div>
              <div className="text-[11px] text-slate-600">Frameworks Aligned</div>
            </div>
            <div className="p-3 rounded-lg bg-white border border-amber-200" data-testid="summary-in-progress">
              <div className="text-2xl font-bold text-amber-800">{counts.in_progress || 0}</div>
              <div className="text-[11px] text-slate-600">In Progress</div>
            </div>
            <div className="p-3 rounded-lg bg-white border border-slate-200" data-testid="summary-planned">
              <div className="text-2xl font-bold text-slate-800">{counts.planned || 0}</div>
              <div className="text-[11px] text-slate-600">Planned (Audit Roadmap)</div>
            </div>
          </div>
          <div className="mt-3 flex gap-2 flex-wrap">
            <Button onClick={downloadStatement} className="bg-emerald-600 hover:bg-emerald-700" data-testid="download-statement-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download Compliance Statement (.txt)
            </Button>
            <a
              href="mailto:compliance@konnect.kaharian.com?subject=Konnect%20DPA%20Request"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100"
              data-testid="request-dpa-link"
            >
              <FileText className="w-3.5 h-3.5" /> Request Data Processing Agreement <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Grid of framework tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="framework-grid">
        {rows.map((f) => (
          <button
            key={f.framework_id}
            onClick={() => setOpen(f)}
            className="text-left p-3 rounded-lg bg-white border border-slate-200 hover:border-emerald-400 hover:shadow-md transition-all group"
            data-testid={`framework-tile-${f.framework_id}`}
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <span className="text-xl">{REGION_FLAGS[f.region] || '🌐'}</span>
              <StatusPill status={f.status} />
            </div>
            <div className="text-sm font-semibold text-slate-900 leading-tight">{f.display_name}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{f.region}</div>
            <div className="text-[11px] text-slate-500 mt-2 line-clamp-2">{f.summary}</div>
            <div className="mt-2 flex items-center gap-1 text-[10px] text-emerald-700 opacity-0 group-hover:opacity-100 transition-opacity">
              View controls <ChevronRight className="w-3 h-3" />
            </div>
          </button>
        ))}
      </div>

      {/* Detail modal */}
      {open && (
        <div
          className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-3 sm:p-4"
          onClick={() => setOpen(null)}
          data-testid="framework-detail-modal"
        >
          <Card className="w-full max-w-3xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="pb-2 sticky top-0 bg-white z-10 border-b border-slate-200">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-lg">{REGION_FLAGS[open.region] || '🌐'}</span>
                    <StatusPill status={open.status} />
                    <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-[10px]">{open.role}</Badge>
                  </div>
                  <CardTitle className="text-base sm:text-lg">{open.display_name}</CardTitle>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {open.region} · <b>{open.authority}</b>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setOpen(null)}>Close</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Summary</div>
                <p className="text-sm text-slate-700 leading-relaxed">{open.summary}</p>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Platform Controls ({open.controls?.length || 0})</div>
                <div className="space-y-2">
                  {(open.controls || []).map((c, i) => (
                    <div key={i} className="p-2.5 rounded-md bg-slate-50 border border-slate-200">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <code className="text-[10px] px-1.5 py-0.5 bg-white rounded border border-slate-200">{c.code}</code>
                            <span className="text-xs font-semibold text-slate-900">{c.title}</span>
                          </div>
                          {c.evidence && (
                            <div className="text-[11px] text-slate-600 mt-1 leading-relaxed"><b>Evidence:</b> {c.evidence}</div>
                          )}
                        </div>
                        <StatusPill status={c.status} size="xs" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {open.audit_roadmap && open.audit_roadmap.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Audit Roadmap</div>
                  <div className="space-y-1.5">
                    {open.audit_roadmap.map((m, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs p-2 rounded-md bg-slate-50 border border-slate-200">
                        <StatusPill status={m.status} size="xs" />
                        <span className="font-medium text-slate-900">{m.date}</span>
                        <span className="text-slate-600">— {m.milestone}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {open.disclaimer && (
                <div className="p-3 rounded-md bg-amber-50 border border-amber-200">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="text-[11px] text-amber-900 leading-relaxed">
                      <b>Disclaimer:</b> {open.disclaimer}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}


// ── Small header seal strip — safe to place near the top of the Studio.
// Renders 4 clickable icons that jump to the Compliance rail item.
export function ComplianceSeals({ onOpenPane }) {
  const seals = [
    { key: 'PDPL',   label: 'PDPL',    Icon: ShieldCheck, tip: 'Bahrain / KSA / UAE / Qatar / Oman PDPL — Aligned' },
    { key: 'GDPR',   label: 'GDPR',    Icon: Lock,        tip: 'EU / UK GDPR — Aligned (SCCs + IDTA)' },
    { key: 'DPA',    label: 'DPA-PH',  Icon: ScrollText,  tip: 'Philippines Data Privacy Act — Aligned' },
    { key: 'AUDIT',  label: 'ISO/SOC', Icon: Globe,       tip: 'ISO 27001 + SOC 2 Type II — Audit roadmap 2026' },
  ];
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="compliance-seals">
      {seals.map((s) => (
        <button
          key={s.key}
          onClick={onOpenPane}
          title={s.tip}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-[10px] font-medium text-emerald-900 transition-colors"
          data-testid={`compliance-seal-${s.key}`}
        >
          <s.Icon className="w-3 h-3" /> {s.label}
        </button>
      ))}
    </div>
  );
}
